import { Tab } from "@headlessui/react";
import React, { FC, useState, useEffect } from "react";
import { StripeElementsOptions, loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import { fetchPaymentIntent } from "../../redux/reducers/Payments/PaymentReducer";
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  CreditCard,
  ShieldCheck,
} from "lucide-react";
import { captureError } from "utils/sentry"; // ← create this file as shown above

export interface CheckOutPagePageMainProps {
  className?: string;
}

// ── Error categorization ──────────────────────────────────
const categorizeStripeError = (
  error: any,
): { title: string; message: string; recoverable: boolean } => {
  const code = error?.code || "";
  const declineCode = error?.decline_code || "";
  const type = error?.type || "";

  // Card declined
  if (type === "card_error") {
    if (declineCode === "insufficient_funds") {
      return {
        title: "Insufficient funds",
        message:
          "Your card has insufficient funds. Please use a different card.",
        recoverable: true,
      };
    }
    if (declineCode === "card_declined" || code === "card_declined") {
      return {
        title: "Card declined",
        message:
          "Your card was declined. Please check your card details or use a different card.",
        recoverable: true,
      };
    }
    if (code === "expired_card") {
      return {
        title: "Card expired",
        message: "Your card has expired. Please use a different card.",
        recoverable: true,
      };
    }
    if (code === "incorrect_cvc") {
      return {
        title: "Incorrect security code",
        message:
          "The security code (CVC) you entered is incorrect. Please check and try again.",
        recoverable: true,
      };
    }
    return {
      title: "Card error",
      message:
        error?.message ||
        "There was an issue with your card. Please try again.",
      recoverable: true,
    };
  }

  // Validation errors
  if (type === "validation_error") {
    return {
      title: "Invalid payment details",
      message:
        error?.message || "Please check your payment details and try again.",
      recoverable: true,
    };
  }

  // Network / API errors
  if (type === "api_connection_error" || type === "api_error") {
    return {
      title: "Connection error",
      message:
        "We couldn't connect to the payment service. Please check your internet connection and try again.",
      recoverable: true,
    };
  }

  // Generic fallback
  return {
    title: "Payment failed",
    message:
      error?.message ||
      "Something went wrong. Please try again or contact support.",
    recoverable: true,
  };
};

// ── Inline Error Component ────────────────────────────────
const PaymentError: React.FC<{
  title: string;
  message: string;
  onDismiss: () => void;
}> = ({ title, message, onDismiss }) => (
  <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3 items-start">
    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-red-800">{title}</p>
      <p className="text-sm text-red-600 mt-0.5 leading-relaxed">{message}</p>
    </div>
    <button
      onClick={onDismiss}
      className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0 text-lg leading-none"
    >
      ×
    </button>
  </div>
);

// ── Success Component ─────────────────────────────────────
const PaymentSuccess: React.FC = () => (
  <div className="rounded-xl border border-green-200 bg-green-50 p-4 flex gap-3 items-center">
    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
    <p className="text-sm font-semibold text-green-800">
      Payment successful! Redirecting...
    </p>
  </div>
);

// ── Main Component ────────────────────────────────────────
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

const CheckOutPagePageMain: FC<CheckOutPagePageMainProps> = ({
  className = "",
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const reserveBooking = useSelector(
    (state: RootState) => state.payment.reserveBooking,
  );
  const totalAmount = Number(reserveBooking?.booking?.total);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentError, setIntentError] = useState<string | null>(null);

  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    throw new Error("Stripe is not initialised");
  }

  // ── Fetch PaymentIntent on page load ──────────────────
  useEffect(() => {
     if (!totalAmount || !reserveBooking?.booking?.id) return; // ← guard
    const payload = {
      booking_id: reserveBooking?.booking?.id,
      payment_intent: {
        currency: "USD",
      },
    };
    dispatch(fetchPaymentIntent(payload)).then((action: any) => {
      if (action?.payload?.success && action?.payload?.client_secret) {
        setClientSecret(action.payload.client_secret);
      } else {
        setIntentError(
          "Unable to initialise payment. Please refresh the page.",
        );
      }
    });
  }, [totalAmount]);

  const options: StripeElementsOptions = {
    clientSecret: clientSecret ?? undefined,
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: "#f5694b",
        colorBackground: "#ffffff",
        colorText: "#1a202c",
        colorDanger: "#ef4444",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        borderRadius: "8px",
      },
    },
  };

  return (
    <div className={`nc-CheckOutPagePageMain ${className}`}>
      <main className="container mt-11 mb-24 lg:mb-32 flex flex-col-reverse lg:flex-row">
        <div className="w-full">
          <div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-8 px-0 sm:p-6 xl:p-8">
            <h2 className="text-3xl lg:text-4xl font-semibold">
              Confirm payment
            </h2>
            <div className="border-b border-neutral-200 dark:border-neutral-700" />
            <div className="mt-6">
              {intentError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3 items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{intentError}</p>
                </div>
              ) : !clientSecret ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#f5694b]" />
                </div>
              ) : (
                <Tab.Group>
                  <Tab.Panels>
                    <Tab.Panel className="space-y-5">
                      <Elements stripe={stripePromise} options={options}>
                        <PaymentForm
                          totalAmount={totalAmount}
                          clientSecret={clientSecret}
                        />
                      </Elements>
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckOutPagePageMain;

// ── Payment Form ──────────────────────────────────────────
const PaymentForm = ({
  totalAmount,
  clientSecret,
}: {
  totalAmount: number;
  clientSecret: string;
}) => {
  const stripe = useStripe();

  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState<{
    title: string;
    message: string;
  } | null>(null);

  const handlePayment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPaymentError(null);
    setLoading(true);

    if (!elements || !stripe) {
      setLoading(false);
      return;
    }

    // Validate form fields
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setLoading(false);
      const categorized = categorizeStripeError(submitError);
      setPaymentError(categorized);
      captureError(submitError, { stage: "elements_submit", totalAmount });
      return;
    }

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/success?payment_method=stripe&redirect_status=succeeded`,
        },
      });

      if (error) {
        const categorized = categorizeStripeError(error);
        setPaymentError(categorized);
        captureError(error, {
          stage: "confirm_payment",
          totalAmount,
          errorCode: error.code,
          errorType: error.type,
        });
      } else {
        setSuccess(true);
      }
    } catch (err: any) {
      const message = err?.message || "An unexpected error occurred";
      setPaymentError({
        title: "Payment error",
        message: `${message}. Please try again or contact support.`,
      });
      captureError(err, { stage: "payment_flow", totalAmount });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-lg mx-auto">
      <form onSubmit={handlePayment} className="flex flex-col space-y-5">
        {/* Payment element with brand styling */}
        <div className="rounded-xl border border-gray-200 dark:border-neutral-700 p-4 bg-white dark:bg-neutral-800">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-4 h-4 text-[#f5694b]" />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Card details
            </span>
          </div>
          <PaymentElement className="w-full" />
        </div>

        {/* Error display */}
        {paymentError && (
          <PaymentError
            title={paymentError.title}
            message={paymentError.message}
            onDismiss={() => setPaymentError(null)}
          />
        )}

        {/* Success display */}
        {success && <PaymentSuccess />}

        {/* Amount summary */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-neutral-800 rounded-xl border border-gray-100 dark:border-neutral-700">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Total to pay
          </span>
          <span className="text-lg font-bold text-gray-900 dark:text-white">
            $
            {Number(totalAmount).toLocaleString("en-US", {
              minimumFractionDigits: 2,
            })}
          </span>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={!stripe || loading || success}
          className={`flex items-center justify-center w-full h-14 bg-[#f5694b] hover:bg-[#e2583d] active:bg-[#d44e32] text-white font-bold rounded-xl transition-all duration-200 text-base shadow-lg hover:shadow-xl ${
            loading || success
              ? "opacity-70 cursor-not-allowed"
              : "hover:scale-[1.01]"
          }`}
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Processing payment...
            </>
          ) : success ? (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              Payment confirmed
            </>
          ) : (
            <>
              <ShieldCheck className="w-5 h-5 mr-2" />
              Pay now
            </>
          )}
        </button>

        {/* Security badge */}
        <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" />
          Payments are secured and encrypted by Stripe
        </p>
      </form>
    </div>
  );
};
